



































import { defineComponent, ref, watch } from '@vue/composition-api';
import { LabelInput, ScanQrCodeIcon } from '@nimiq/vue-components';
import { parseRequestLink, Currency } from '@nimiq/utils';
import { useI18n } from '@/lib/useI18n';
import { nextTick } from '@/lib/nextTick';
import { loadBitcoinJS } from '../lib/BitcoinJSLoader';
import { ENV_MAIN } from '../lib/Constants';
import { normalizeAddress, validateAddress } from '../lib/BitcoinTransactionUtils';
import {
    isValidDomain as isValidUnstoppableDomain,
    resolve as resolveUnstoppableDomain,
} from '../lib/UnstoppableDomains';
import { useConfig } from '../composables/useConfig';

const BtcAddressInput = defineComponent({
    props: {
        placeholder: {
            type: String,
            required: false,
        },
        value: {
            type: String,
            default: '',
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    setup(props, context) {
        const { $t } = useI18n();
        const { config } = useConfig();

        const input$ = ref<LabelInput>(null);
        const inputFontSizeScaleFactor = ref(1);
        let inputPadding: number | null = null;

        const address = ref('');
        const invalid = ref(false);

        const isResolvingUnstoppableDomain = ref(false);
        const resolverError = ref('');

        function focus() {
            if (!input$.value) return;
            input$.value.focus();
        }

        async function checkAddress() {
            resolverError.value = '';

            // Detect unstoppable domains
            if (isValidUnstoppableDomain(address.value)) {
                isResolvingUnstoppableDomain.value = true;
                const domain = address.value;
                const ticker = config.environment === ENV_MAIN ? 'BTC' : 'TBTC';
                try {
                    const [resolvedAddress] = await Promise.all([
                        resolveUnstoppableDomain(domain, ticker),
                        loadBitcoinJS(),
                    ]);
                    const normalizedAddress = resolvedAddress && normalizeAddress(resolvedAddress);
                    if (normalizedAddress && validateAddress(normalizedAddress)) {
                        context.emit('domain-address', domain, normalizedAddress);
                        invalid.value = false;
                    } else {
                        resolverError.value = $t('Domain does not resolve to a valid address') as string;
                        invalid.value = true;
                    }
                } catch (e) {
                    console.debug(e); // eslint-disable-line no-console
                    let message = e instanceof Error ? e.message : String(e);
                    message = message.replace(`crypto.${ticker}.address record`, `${ticker} address`);
                    resolverError.value = message;
                    invalid.value = true;
                } finally {
                    isResolvingUnstoppableDomain.value = false;
                }

                return;
            }

            // BTC addresses are at least 26 characters long
            if (address.value.length < 26) {
                invalid.value = false;
                return;
            }

            const normalizedAddress = normalizeAddress(address.value);
            await loadBitcoinJS();
            if (validateAddress(normalizedAddress)) {
                context.emit('address', normalizedAddress);
                invalid.value = false;
            } else {
                invalid.value = true;
            }
        }

        async function updateInputFontSize() {
            await nextTick();

            if (!input$.value) return;

            if (!inputPadding) {
                inputPadding = parseInt(
                    window.getComputedStyle(input$.value.$el.children[2])
                        .getPropertyValue('padding-left')
                        .replace('px', ''),
                    10,
                );
            }

            inputFontSizeScaleFactor.value = 1;
            await nextTick();

            const width = input$.value.$el.children[1].clientWidth;
            const maxWidth = input$.value.$el.children[2].clientWidth - (inputPadding / 2);

            inputFontSizeScaleFactor.value = Math.max(Math.min(maxWidth / width, 1), 0.7);
        }

        function onUpdate() {
            checkAddress();
            updateInputFontSize();
        }

        function onInput() {
            context.emit('input', address.value);
            onUpdate();
        }

        function onPaste(event: ClipboardEvent) {
            const { clipboardData } = event;
            const pastedData = clipboardData ? clipboardData.getData('text/plain') : '';

            const bitcoinRequestLink = parseRequestLink(pastedData, {
                currencies: [Currency.BTC],
                normalizeAddress: {
                    [Currency.BTC]: normalizeAddress,
                },
                // No need to pass isValidAddress (which would require loading BitcoinJS) because validity will be
                // checked in onUpdate, too.
            });
            if (bitcoinRequestLink) {
                event.preventDefault();
                address.value = bitcoinRequestLink.recipient;
                onUpdate();
            }

            context.emit('paste', event, pastedData);
        }

        watch(() => props.value, () => {
            address.value = props.value;
            onUpdate();
        });

        return {
            focus, // exposed for use from other components

            input$,
            address,
            invalid,
            onPaste,
            onInput,
            inputFontSizeScaleFactor,
            isResolvingUnstoppableDomain,
            resolverError,
        };
    },
    components: {
        LabelInput,
        ScanQrCodeIcon,
    },
});
// Export the component's instance type alongside the value (the constructor) via Typescript declaration merging,
// similar to what would be the case for a class-based component declaration, for convenient usage in Ref types.
type BtcAddressInput = InstanceType<typeof BtcAddressInput>;
export default BtcAddressInput;
